/**
 * Layer-property intents that need no lazy editor subsystem: layer effects
 * and adjustment settings. Kept apart from content.ts (which imports the
 * vector runtime) so the UI's user-dispatch path can load them eagerly.
 */
import type { AdjustmentInput, AdjustmentUpdateIntent, EffectSetIntent } from '@maipo/agent-protocol';
import { UpdateAdjustmentCommand } from '../../core/commands';
import { findLayer, type AdjustmentLayer } from '../../core/model';
import { UpdateLayerStyleCommand, layerStyle, type LayerStyle } from '../../core/layer-styles';
import { DEFAULT_BLACK_WHITE } from '../../filters/tone';
import { defineIntent, IntentRejection, type Diagnostic } from '../registry';
import { checkUnlocked, error, layerFor, requireLayer, resolveRef, staticIds } from './common';

export function same(left: unknown, right: unknown): boolean {
  return JSON.stringify(left) === JSON.stringify(right);
}

export function singleLayerFootprint(ref: string, aliases: ReadonlyMap<string, string>) {
  const ids = staticIds([ref], aliases);
  return { reads: ids, writes: ids, structural: false, document: false };
}

export const effectSet = defineIntent<EffectSetIntent>({
  type: 'effect.set',
  version: 1,
  footprint: (_document, intent, aliases) => singleLayerFootprint(intent.layer, aliases),
  validate: (document, intent, context) => {
    const diagnostics: Diagnostic[] = [];
    const layer = layerFor(document, intent.layer, context, diagnostics);
    if (!layer) return diagnostics;
    if (context.policy.kinds === 'strict' && layer.kind === 'adjustment') diagnostics.push(error('UNSUPPORTED_FOR_KIND', `Adjustment layer ${layer.id} cannot carry layer styles.`, layer.id));
    checkUnlocked(document, layer, 'any', diagnostics, context.policy);
    return diagnostics;
  },
  compile: (document, intent, _prepared, aliases) => {
    const layer = requireLayer(document, intent.layer, aliases);
    const style: LayerStyle = layerStyle(layer);
    // Merge onto the stored effect so its key order and any fields this
    // protocol version does not model survive, and keep a stored colour whose
    // only difference is letter case: re-setting an effect to its current
    // value must not rewrite document bytes.
    const colour = (current: { color?: unknown } | undefined, next: string) => typeof current?.color === 'string' && current.color.toLowerCase() === next.toLowerCase() ? current.color : next.toLowerCase();
    if (intent.value === null) delete style[intent.effect];
    else if (intent.effect === 'dropShadow') style.dropShadow = { ...style.dropShadow, enabled: intent.value.enabled ?? true, color: colour(style.dropShadow, intent.value.color), opacity: intent.value.opacity, blur: intent.value.blur, offsetX: intent.value.offsetX, offsetY: intent.value.offsetY };
    else style.stroke = { ...style.stroke, enabled: intent.value.enabled ?? true, color: colour(style.stroke, intent.value.color), opacity: intent.value.opacity, width: intent.value.width };
    return [new UpdateLayerStyleCommand(layer.id, style)];
  },
  describe: (document, intent, aliases) => {
    const id = resolveRef(intent.layer, aliases) ?? intent.layer;
    const layer = findLayer(document.root, id);
    return [{ layer: id, path: `effects.${intent.effect}`, before: layer ? layerStyle(layer)[intent.effect] ?? null : null, after: intent.value }];
  },
});

type Adjustment = AdjustmentLayer['adjustment'];

/** The adjustment after applying `input`; nested settings merge onto the current (or default) values. */
export function mergedAdjustment(current: Adjustment, input: AdjustmentInput): Partial<Adjustment> {
  const patch: Partial<Adjustment> = {};
  if (input.brightness !== undefined) patch.brightness = input.brightness;
  if (input.contrast !== undefined) patch.contrast = input.contrast;
  if (input.saturation !== undefined) patch.saturation = input.saturation;
  if (input.invert !== undefined) patch.invert = input.invert;
  if (input.levels) patch.levels = { ...input.levels };
  if (input.curves) {
    const base = current.curves ?? { rgb: [], red: [], green: [], blue: [] };
    patch.curves = {
      rgb: (input.curves.rgb ?? base.rgb).map((point) => ({ ...point })),
      red: (input.curves.red ?? base.red).map((point) => ({ ...point })),
      green: (input.curves.green ?? base.green).map((point) => ({ ...point })),
      blue: (input.curves.blue ?? base.blue).map((point) => ({ ...point })),
    };
  }
  if (input.gradientMap) {
    const base = current.gradientMap ?? { enabled: false, reverse: false, stops: [{ position: 0, color: '#000000' }, { position: 1, color: '#ffffff' }] };
    patch.gradientMap = {
      enabled: input.gradientMap.enabled ?? base.enabled,
      reverse: input.gradientMap.reverse ?? base.reverse,
      stops: (input.gradientMap.stops ?? base.stops).map((stop) => ({ position: stop.position, color: stop.color.toLowerCase() })),
    };
  }
  if (input.blackWhite) patch.blackWhite = { ...(current.blackWhite ?? DEFAULT_BLACK_WHITE), ...input.blackWhite };
  return patch;
}

/** Cross-field rules the schema cannot express; the project format refuses documents that break them. */
export function validateAdjustment(current: Adjustment, input: AdjustmentInput): Diagnostic[] {
  const diagnostics: Diagnostic[] = [];
  const levels = input.levels;
  if (levels && levels.inputBlack >= levels.inputWhite) diagnostics.push(error('COMMAND_REJECTED', 'Levels input black must be below input white.'));
  if (levels && levels.outputBlack > levels.outputWhite) diagnostics.push(error('COMMAND_REJECTED', 'Levels output black must not exceed output white.'));
  const merged = mergedAdjustment(current, input);
  if (merged.gradientMap && merged.gradientMap.stops.length < 2) diagnostics.push(error('COMMAND_REJECTED', 'A gradient map needs at least two stops.'));
  return diagnostics;
}

export const adjustmentUpdate = defineIntent<AdjustmentUpdateIntent>({
  type: 'adjustment.update',
  version: 1,
  footprint: (_document, intent, aliases) => singleLayerFootprint(intent.layer, aliases),
  validate: (document, intent, context) => {
    const diagnostics: Diagnostic[] = [];
    const layer = layerFor(document, intent.layer, context, diagnostics);
    if (!layer) return diagnostics;
    if (layer.kind !== 'adjustment') { diagnostics.push(error('UNSUPPORTED_FOR_KIND', `Layer ${layer.id} is a ${layer.kind} layer, not an adjustment.`, layer.id)); return diagnostics; }
    diagnostics.push(...validateAdjustment(layer.adjustment, intent.set).map((diagnostic) => ({ ...diagnostic, layer: layer.id })));
    checkUnlocked(document, layer, 'any', diagnostics, context.policy);
    return diagnostics;
  },
  compile: (document, intent, _prepared, aliases) => {
    const layer = requireLayer(document, intent.layer, aliases);
    if (layer.kind !== 'adjustment') throw new IntentRejection('UNSUPPORTED_FOR_KIND', `Layer ${layer.id} is not an adjustment.`, layer.id);
    const patch = mergedAdjustment(layer.adjustment, intent.set);
    if (same({ ...layer.adjustment, ...patch }, layer.adjustment)) return [];
    return [new UpdateAdjustmentCommand(layer.id, patch)];
  },
  describe: (document, intent, aliases) => {
    const id = resolveRef(intent.layer, aliases) ?? intent.layer;
    const layer = findLayer(document.root, id);
    const current = layer?.kind === 'adjustment' ? layer.adjustment : undefined;
    const patch = current ? mergedAdjustment(current, intent.set) : intent.set;
    return (Object.entries(patch) as [string, unknown][]).map(([key, after]) => ({ layer: id, path: `adjustment.${key}`, before: current ? current[key as keyof Adjustment] ?? null : null, after }));
  },
});
